"use client"

import { useState } from "react"
import { m, AnimatePresence } from "framer-motion"
import { ChevronRightSmIcon } from "@/components/ui/icons"
import { cn } from "@/lib/utils"

export interface RelatedItem {
  slug: string
  name: string
  /** Optional — diseases may have no description; the expanded panel then shows only the link. */
  description?: string | null
  /** Pre-resolved localised detail URL (server resolves so no function crosses the boundary). */
  href: string
}

interface RelatedListProps {
  items: RelatedItem[]
  /** Label prefix for the "More about X" link, e.g. "More about". */
  moreAboutLabel: string
}

/**
 * Expand-on-click row list for related content on a detail page (biomarkers,
 * diseases). Matches the pattern used on the /biomarkers listing page: row
 * label + chevron, expands to show the item's description plus a link to its
 * individual detail page.
 */
export function RelatedList({ items, moreAboutLabel }: RelatedListProps) {
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null)

  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-1 md:grid-cols-2">
      {items.map((item) => {
        const isOpen = expandedSlug === item.slug
        return (
          <div key={item.slug} className="border-b border-neutral-100">
            <button
              type="button"
              onClick={() => setExpandedSlug(isOpen ? null : item.slug)}
              aria-expanded={isOpen}
              className="hover:text-aubergine-900 group flex w-full cursor-pointer items-center justify-between py-2.5 text-left text-base text-neutral-700 transition-colors"
            >
              <span>{item.name}</span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                className={cn(
                  "group-hover:text-aubergine-500 text-neutral-300 transition-all duration-300",
                  isOpen && "text-aubergine-500 rotate-90"
                )}
                aria-hidden="true"
              >
                <path d="M5 3l4 4-4 4" />
              </svg>
            </button>
            {/* safe-animate-presence: conditional-mount — gated by `isOpen=true` */}
            <AnimatePresence>
              {isOpen && (
                <m.div
                  initial={{ opacity: 0, height: 0, scale: 0.97, y: 12 }}
                  animate={{ opacity: 1, height: "auto", scale: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0, scale: 0.97, y: 12 }}
                  transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
                  className="overflow-hidden"
                >
                  <div className="pr-8 pb-3">
                    {item.description && (
                      <p className="type-body-md leading-relaxed text-neutral-500">
                        {item.description}
                      </p>
                    )}
                    <a
                      href={item.href}
                      className="text-aubergine-700 hover:text-aubergine-900 mt-2 inline-flex items-center gap-1 text-sm font-medium transition-colors"
                    >
                      {moreAboutLabel} {item.name}
                      <ChevronRightSmIcon />
                    </a>
                  </div>
                </m.div>
              )}
            </AnimatePresence>
          </div>
        )
      })}
    </div>
  )
}
